/**
 * Settings → Display.
 *
 * The Appearance dialog's contents, moved onto a page. Same three preferences,
 * the same utils, the same live-apply-and-persist behaviour — nothing about how
 * they work changed, only where they are and how much room they get.
 *
 * The three ARE the app's display preferences: colour theme, font, text size.
 * There is no density setting and no sidebar preference to expose, so none is
 * offered; a control that writes nowhere would be worse than its absence.
 */
import { useEffect, useState } from 'react';
import { useTheme } from '../../utils/theme';
import { THEME_MODES, type ThemeMode } from '../../utils/theme.utils';
import {
  APP_FONTS,
  APP_PRESETS,
  getFont,
  getScale,
  getDensity,
  getWeight,
  setFont as applyFont,
  setScale,
  setDensity as applyDensity,
  setWeight as applyWeight,
  applyPreset,
  adjustScale,
  resetAppearance,
  SCALE_MIN,
  SCALE_MAX,
  SCALE_STEP,
  type AppFont,
  type AppDensity,
  type AppWeight,
  type StylePreset,
} from '../../utils/appearance.utils';
import SettingsLayout, { SettingsSection } from './settings-layout';

/** Display names and swatches for the themes the app ships. */
const THEME_META: Record<string, { label: string; dot: string }> = {
  light: { label: 'Light', dot: '#4F46E5' },
  dark: { label: 'Iris', dot: '#2BA9E8' },
  editorial: { label: 'Editorial', dot: '#C2502D' },
  meadow: { label: 'Meadow', dot: '#3F8D78' },
  royal: { label: 'Lavender', dot: '#8B5CF6' },
  sunset: { label: 'Rose', dot: '#FB7185' },
  ocean: { label: 'Teal', dot: '#14B8A6' },
};

const DisplaySettings = () => {
  const { mode, setThemeMode } = useTheme();
  const [font, setFontState] = useState<AppFont>(getFont);
  const [scale, setScaleState] = useState<number>(getScale);
  const [density, setDensityState] = useState<AppDensity>(getDensity);
  const [weight, setWeightState] = useState<AppWeight>(getWeight);

  // Another tab, or the reset below, can move these underneath us.
  useEffect(() => {
    setFontState(getFont());
    setScaleState(getScale());
    setDensityState(getDensity());
    setWeightState(getWeight());
  }, []);

  const pickFont = (f: AppFont) => { applyFont(f); setFontState(f); };
  const pickDensity = (d: AppDensity) => { applyDensity(d); setDensityState(d); };
  const pickWeight = (w: AppWeight) => { applyWeight(w); setWeightState(w); };
  const pickPreset = (p: StylePreset) => {
    applyPreset(p);
    setFontState(getFont());
    setDensityState(getDensity());
    setWeightState(getWeight());
  };
  // A preset is "active" when the three knobs all match it.
  const activePreset = APP_PRESETS.find(
    (p) => p.font === font && p.density === density && p.weight === weight,
  )?.key;

  const reset = () => {
    resetAppearance();
    setFontState(getFont());
    setScaleState(getScale());
    setDensityState(getDensity());
    setWeightState(getWeight());
  };

  const choice = (active: boolean) =>
    `flex items-center gap-2 rounded-lg border px-3 py-2 text-[13px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
      active
        ? 'border-indigo-400 bg-indigo-50 dark:bg-indigo-400/10 text-indigo-700 dark:text-indigo-200 font-medium'
        : 'border-line text-gray-700 dark:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
    }`;

  return (
    <SettingsLayout
      title="Display"
      description="Customize how the application looks."
      testId="SCR-DisplaySettings"
    >
      <SettingsSection title="Style preset" hint="One click sets font, spacing and weight together.">
        <div role="radiogroup" aria-label="Style preset" className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {APP_PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              role="radio"
              aria-checked={activePreset === p.key}
              onClick={() => pickPreset(p)}
              className={`${choice(activePreset === p.key)} justify-between`}
            >
              <span className="flex flex-col items-start text-left leading-tight">
                <span className="font-medium">{p.label}</span>
                <span className="text-[11px] text-ink-muted">{p.hint}</span>
              </span>
              {activePreset === p.key && <i className="pi pi-check text-[11px]" aria-hidden="true" />}
            </button>
          ))}
        </div>
      </SettingsSection>

      <SettingsSection title="Colour theme" hint="Applies immediately across the application.">
        <div
          role="radiogroup"
          aria-label="Colour theme"
          className="grid grid-cols-2 sm:grid-cols-3 gap-2"
        >
          {THEME_MODES.map((m: ThemeMode) => {
            const meta = THEME_META[m] ?? { label: m, dot: '#94A3B8' };
            const active = mode === m;
            return (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => setThemeMode(m)}
                className={choice(active)}
              >
                <span
                  aria-hidden="true"
                  className="w-3.5 h-3.5 rounded-full flex-none ring-1 ring-black/5"
                  style={{ background: meta.dot }}
                />
                <span className="truncate">{meta.label}</span>
                {active && <i className="pi pi-check ml-auto text-[11px]" aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      </SettingsSection>

      <SettingsSection title="Font" hint="Each option previews itself.">
        <div role="radiogroup" aria-label="Font" className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {APP_FONTS.map((f) => (
            <button
              key={f.value}
              type="button"
              role="radio"
              aria-checked={font === f.value}
              onClick={() => pickFont(f.value)}
              style={{ fontFamily: f.stack }}
              className={`${choice(font === f.value)} justify-between`}
            >
              <span className="truncate">{f.label}</span>
              {font === f.value && <i className="pi pi-check text-[11px]" aria-hidden="true" />}
            </button>
          ))}
        </div>
      </SettingsSection>

      <SettingsSection title="Density" hint="Row and card spacing — Comfortable adds air.">
        <div role="radiogroup" aria-label="Density" className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {([['compact', 'Compact'], ['comfortable', 'Comfortable']] as [AppDensity, string][]).map(
            ([v, l]) => (
              <button
                key={v}
                type="button"
                role="radio"
                aria-checked={density === v}
                onClick={() => pickDensity(v)}
                className={`${choice(density === v)} justify-between`}
              >
                <span>{l}</span>
                {density === v && <i className="pi pi-check text-[11px]" aria-hidden="true" />}
              </button>
            ),
          )}
        </div>
      </SettingsSection>

      <SettingsSection title="Text weight" hint="Body weight; headings keep their own.">
        <div role="radiogroup" aria-label="Text weight" className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {([['regular', 'Regular'], ['medium', 'Medium']] as [AppWeight, string][]).map(([v, l]) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={weight === v}
              onClick={() => pickWeight(v)}
              className={`${choice(weight === v)} justify-between`}
              style={{ fontWeight: v === 'medium' ? 500 : 400 }}
            >
              <span>{l}</span>
              {weight === v && <i className="pi pi-check text-[11px]" aria-hidden="true" />}
            </button>
          ))}
        </div>
      </SettingsSection>

      <SettingsSection title="Text size" hint="Scales text throughout the application.">
        <div className="flex items-center gap-4 max-w-md">
          <button
            type="button"
            onClick={() => setScaleState(adjustScale(-SCALE_STEP))}
            disabled={scale <= SCALE_MIN}
            aria-label="Decrease text size"
            className="grid place-items-center w-8 h-8 shrink-0 rounded-lg border border-line text-gray-600 dark:text-gray-300 text-lg leading-none disabled:opacity-40 hover:border-gray-300 dark:hover:border-gray-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          >
            −
          </button>
          <div className="flex-1 min-w-0">
            <input
              type="range"
              min={SCALE_MIN}
              max={SCALE_MAX}
              step={SCALE_STEP}
              value={scale}
              onChange={(e) => setScaleState(setScale(Number(e.target.value)))}
              aria-label="Text size"
              aria-valuetext={`${scale} percent`}
              className="w-full accent-indigo-600"
            />
          </div>
          <button
            type="button"
            onClick={() => setScaleState(adjustScale(SCALE_STEP))}
            disabled={scale >= SCALE_MAX}
            aria-label="Increase text size"
            className="grid place-items-center w-8 h-8 shrink-0 rounded-lg border border-line text-gray-600 dark:text-gray-300 text-lg leading-none disabled:opacity-40 hover:border-gray-300 dark:hover:border-gray-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          >
            +
          </button>
          <span className="w-12 text-right text-[13px] font-medium text-gray-700 dark:text-gray-200 tabular-nums">
            {scale}%
          </span>
        </div>
      </SettingsSection>

      {/* These persist as they are changed — there is nothing to submit, so
          saying so is more useful than a Save button that would do nothing. */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-700/60">
        <span className="text-[12px] text-gray-400">Changes are saved automatically.</span>
        <button
          type="button"
          onClick={reset}
          className="text-[13px] text-ink-muted hover:text-gray-800 dark:hover:text-gray-200 transition-colors rounded px-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
        >
          Reset to defaults
        </button>
      </div>
    </SettingsLayout>
  );
};

export default DisplaySettings;
